import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'

import { Button } from 'components/atoms/button'
import { IconSvg } from 'components/common'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { useCountdown } from 'hooks/use-countdown'
import { useInstagramConnectionPolling } from 'hooks/use-instagram-connection-polling'
import { useOpenAppLink } from 'hooks/use-open-app-link'
import { useStore } from 'hooks/useStore'
import { useStartInstagramVerificationMutation } from 'graphql/generated/scheme'
import { trueErrorMessage } from 'utils/error'

// The server supplies the DM link; only ever open Instagram's own hosts or its app scheme.
const DM_LINK_HOSTS = new Set(['instagram.com', 'www.instagram.com', 'm.instagram.com', 'ig.me'])

function isAllowedDmLink(link: string | null | undefined): link is string {
	if (!link) return false
	try {
		const url = new URL(link)
		return url.protocol === 'instagram:' || (url.protocol === 'https:' && DM_LINK_HOSTS.has(url.hostname))
	} catch {
		return false
	}
}

export interface InstagramVerificationModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	onConnected?: () => void
}

interface StepProps {
	number: number
	title: string
	state: 'upcoming' | 'current' | 'done'
	isLast?: boolean
	children?: ReactNode
}

function Step({ number, title, state, isLast = false, children }: Readonly<StepProps>) {
	return (
		<li className="flex gap-x-3" aria-current={state === 'current' ? 'step' : undefined}>
			<div className="flex flex-col items-center">
				<span
					className={clsx(
						'flex items-center justify-center size-7 rounded-full shrink-0 text-xs font-bold transition-colors duration-300',
						state === 'done' && 'bg-secondary-green text-theme-surface',
						state === 'current' && 'bg-accent-surface text-primary-text',
						state === 'upcoming' && 'bg-white/10 text-white/50'
					)}
				>
					{state === 'done' ? <FontAwesomeIcon icon={['fas', 'check']} size="sm" /> : number}
					<span className="sr-only">{state === 'done' ? ' (done)' : ''}</span>
				</span>
				{!isLast && <span className="w-px flex-1 mt-1 bg-white/10" aria-hidden="true" />}
			</div>
			<div className={clsx('min-w-0 flex-1 space-y-2', !isLast && 'pb-5')}>
				<h3
					className={clsx(
						'text-sm font-bold leading-7 transition-colors duration-300',
						state === 'upcoming' ? 'text-white/50' : 'text-primary-text'
					)}
				>
					{title}
				</h3>
				{children}
			</div>
		</li>
	)
}

interface VerificationState {
	code: string
	expiresAt: string
	dmDeepLink: string | null
}

export function InstagramVerificationModal({ open, setOpen, onConnected }: InstagramVerificationModalProps) {
	const { refetchUser } = useStore()
	const [verification, setVerification] = useState<VerificationState | null>(null)
	const [requestFailed, setRequestFailed] = useState(false)
	// Set while a new code is being fetched after the last one expired or failed, so the retry button keeps its place
	const [retrying, setRetrying] = useState(false)
	// Progress through the steps for the current code; both reset whenever a new code is issued
	const [copied, setCopied] = useState(false)
	const [dmOpened, setDmOpened] = useState(false)
	// Guards against duplicate code requests from StrictMode double-invoked effects
	const requestInFlightRef = useRef(false)
	const openRef = useRef(open)
	openRef.current = open

	const [startInstagramVerification, { loading: requestingCode }] = useStartInstagramVerificationMutation({
		onCompleted: data => {
			setRetrying(false)
			// Ignore responses that land after the modal was closed
			if (!openRef.current) return
			const result = data?.user?.socialMedia?.startInstagramVerification
			if (result) {
				setRequestFailed(false)
				setVerification({
					code: result.code,
					expiresAt: result.expiresAt,
					dmDeepLink: result.dmDeepLink ?? null,
				})
			} else {
				// A response with no code would otherwise leave the modal stuck on a placeholder
				setRequestFailed(true)
			}
		},
		onError: error => {
			setRetrying(false)
			if (!openRef.current) return
			setRequestFailed(true)
			toast.error(trueErrorMessage(error))
		},
	})

	const { isExpired, formatted } = useCountdown(verification?.expiresAt ?? null)

	const handleConnected = useCallback(() => {
		if (!openRef.current) return
		refetchUser()
		toast.success('Instagram account connected!')
		onConnected?.()
		setOpen(false)
	}, [refetchUser, setOpen, onConnected])

	const {
		start: startPolling,
		stop: stopPolling,
		isPolling,
	} = useInstagramConnectionPolling({
		onConnected: handleConnected,
	})

	const requestCode = useCallback(async () => {
		if (requestInFlightRef.current) return
		requestInFlightRef.current = true
		setRequestFailed(false)
		setVerification(null)
		setCopied(false)
		setDmOpened(false)
		try {
			await startInstagramVerification()
		} finally {
			requestInFlightRef.current = false
		}
	}, [startInstagramVerification])

	useEffect(() => {
		if (!open) return
		requestCode()
		startPolling()
		return () => {
			stopPolling()
		}
	}, [open, requestCode, startPolling, stopPolling])

	const showGetNewCode = isExpired || (requestFailed && !requestingCode) || retrying
	const canUseCode = !!verification?.code && !requestingCode && !isExpired

	// Starts the clipboard write synchronously so it stays inside the click's user gesture
	// (Safari rejects it once we've awaited anything); the outcome is reported when it settles.
	const copyCode = () => {
		if (!verification?.code) return
		const notifyCopyFailed = () => toast.error('Couldn’t copy. Select the code and copy it manually.')
		// navigator.clipboard is undefined in insecure contexts and some webviews
		const { clipboard } = navigator
		if (!clipboard) {
			notifyCopyFailed()
			return
		}
		clipboard
			.writeText(verification.code)
			.then(() => {
				setCopied(true)
				toast.success('Code copied — paste it into the DM')
			})
			.catch(notifyCopyFailed)
	}

	const openAppLink = useOpenAppLink()

	const handleOpenInstagramDm = () => {
		const link = verification?.dmDeepLink
		if (!isAllowedDmLink(link)) return

		// Copy for them so the code is on the clipboard by the time the DM opens
		if (!copied) copyCode()
		setDmOpened(true)

		// https links are real web URLs — window.open already gives us "app if
		// installed, browser otherwise" via the OS's universal link handling, and
		// keeps this tab (and its DM-connection polling) alive either way.
		if (!link.startsWith('instagram:')) {
			window.open(link, '_blank', 'noopener,noreferrer')
			return
		}

		// instagram: is a custom scheme with no browser fallback of its own, so fall
		// back to the web DM inbox if the app doesn't take over. Skip the fallback if
		// the modal's since been closed (e.g. the DM already connected via polling).
		openAppLink(link, 'https://www.instagram.com/direct/inbox/', () => openRef.current)
	}

	// Step 2 stays current after the DM opens: sending the message is still on them, and step 3 only waits
	const currentStep = copied ? 2 : 1
	const stepState = (step: number) => {
		if (step < currentStep) return 'done'
		return step === currentStep ? 'current' : 'upcoming'
	}

	return (
		<BaseModal open={open} setOpen={setOpen} title="Connect Instagram" size="md">
			<div className="space-y-5 text-left">
				<div className="flex items-center gap-x-3">
					<div className="flex items-center justify-center w-10 h-10 bg-white/10 rounded-full shrink-0">
						<IconSvg icon="instagram" />
					</div>
					<p className="text-sm text-white/50 leading-5">
						Send us a one-time code by direct message to prove this Instagram account is yours.
					</p>
				</div>

				<ol className="list-none">
					<Step number={1} title="Copy your code" state={stepState(1)}>
						<button
							type="button"
							onClick={copyCode}
							disabled={!canUseCode}
							aria-label={verification ? `Copy code ${verification.code}` : 'Copy code'}
							className={clsx(
								'w-full min-h-14 flex items-center justify-between gap-x-3 px-4 py-3 rounded-xl bg-white/10 border-[0.6px] border-solid border-translucent-plus-border hover:bg-tint-surface transition-colors duration-300 touch-manipulation disabled:cursor-not-allowed',
								!canUseCode && !requestingCode && 'opacity-40'
							)}
						>
							{requestingCode ? (
								<span className="flex-1 flex justify-center">
									<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin />
								</span>
							) : (
								<>
									<span className="min-w-0 truncate font-mono text-xl font-bold tracking-[0.25em] text-primary-text">
										{verification?.code ?? '------'}
									</span>
									<span className="flex items-center gap-x-1.5 text-xs font-bold text-secondary-text shrink-0">
										{copied ? (
											<>
												<FontAwesomeIcon
													icon={['fas', 'check']}
													className="text-secondary-green"
												/>
												Copied
											</>
										) : (
											<>
												<IconSvg icon="copy" />
												Copy
											</>
										)}
									</span>
								</>
							)}
						</button>
						<div className="min-h-5 text-xs leading-5 text-white/50">
							{requestFailed && !requestingCode && (
								<output className="text-secondary-red">Couldn’t get a code. Try again.</output>
							)}
							{verification && !requestingCode && !isExpired && (
								<>
									Expires in <span className="text-primary-text font-medium">{formatted}</span>
								</>
							)}
							{verification && !requestingCode && isExpired && (
								<output className="text-secondary-red">This code has expired.</output>
							)}
						</div>
					</Step>

					<Step number={2} title="Send it to us on Instagram" state={stepState(2)} isLast>
						<p className="text-sm leading-5 text-white/50">
							Paste the code into the chat and send it, from the Instagram account you want to connect.
						</p>
						{showGetNewCode ? (
							<Button
								variant="primary"
								fullWidth
								onClick={() => {
									setRetrying(true)
									void requestCode()
								}}
								disabled={requestingCode}
							>
								{requestingCode && <FontAwesomeIcon icon={['fas', 'spinner']} spin className="mr-2" />}
								Get New Code
							</Button>
						) : (
							<Button
								variant="primary"
								fullWidth
								onClick={handleOpenInstagramDm}
								disabled={!isAllowedDmLink(verification?.dmDeepLink) || !canUseCode}
							>
								Copy & Open Instagram
							</Button>
						)}
					</Step>
				</ol>

				<div className="space-y-1">
					<output className="flex items-start justify-center gap-x-2 text-sm leading-5 text-white/50">
						<span
							className={clsx(
								'size-2 mt-1.5 rounded-full shrink-0',
								isPolling && !isExpired && dmOpened ? 'bg-secondary-green animate-pulse' : 'bg-white/30'
							)}
							aria-hidden="true"
						/>
						{dmOpened && !isExpired
							? 'Waiting for your message. Keep this window open.'
							: 'We’ll connect automatically when your message arrives.'}
					</output>
					<button
						type="button"
						onClick={() => requestCode()}
						disabled={requestingCode || showGetNewCode}
						className={clsx(
							'w-full min-h-11 text-sm text-link hover:text-link-hover disabled:text-secondary-text disabled:cursor-not-allowed touch-manipulation',
							showGetNewCode && 'invisible'
						)}
					>
						Code not working? Get a new one
					</button>
				</div>
			</div>
		</BaseModal>
	)
}
